import React from 'react';
import { FileText, Shield, Zap, Lock, Users, TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';

const Whitepaper = () => {
  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="font-unbounded text-4xl font-bold text-text-primary mb-2">
          Whitepaper
        </h1>
        <p className="text-text-secondary">Technical documentation for NATIVE Token</p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-4xl glass p-8 rounded-sm"
      >
        <div className="prose prose-invert max-w-none">
          <div className="mb-8">
            <h2 className="font-unbounded text-3xl font-bold text-primary mb-4">
              NATIVE Token Blockchain
            </h2>
            <p className="text-text-secondary mb-2">Version 2.0.0 • March 2026</p>
            <p className="text-text-muted text-sm">A Proof-of-Work blockchain with native token support and token factory capabilities</p>
          </div>

          <section className="mb-8 pb-8 border-b border-border">
            <h3 className="font-unbounded text-2xl font-bold text-text-primary mb-4 flex items-center gap-3">
              <FileText size={24} className="text-primary" />
              Abstract
            </h3>
            <p className="text-text-secondary leading-relaxed">
              The NATIVE Token Blockchain is a decentralized, proof-of-work blockchain platform designed for educational and testing purposes. It features a native cryptocurrency (NATIVE) with built-in minting and burning mechanisms, along with a token factory that allows users to create and deploy custom tokens on the network. The blockchain implements standard cryptographic security measures including ECDSA for wallet management and SHA-256 for block hashing.
            </p>
          </section>

          <section className="mb-8 pb-8 border-b border-border">
            <h3 className="font-unbounded text-2xl font-bold text-text-primary mb-4 flex items-center gap-3">
              <Shield size={24} className="text-accent-success" />
              Security Model
            </h3>
            <div className="space-y-4 text-text-secondary leading-relaxed">
              <p>
                The blockchain uses <strong className="text-text-primary">Proof-of-Work (PoW)</strong> consensus with adjustable difficulty. Each block must satisfy the difficulty requirement by finding a nonce that produces a hash with the required number of leading zeros.
              </p>
              <div className="glass p-4 rounded-sm">
                <h4 className="font-bold text-text-primary mb-2">Key Security Features:</h4>
                <ul className="list-disc list-inside space-y-2 text-sm">
                  <li>ECDSA (SECP256k1) for wallet key pairs</li>
                  <li>SHA-256 cryptographic hashing</li>
                  <li>Transaction signature verification</li>
                  <li>Chain validation and integrity checks</li>
                  <li>Immutable transaction history</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8 pb-8 border-b border-border">
            <h3 className="font-unbounded text-2xl font-bold text-text-primary mb-4 flex items-center gap-3">
              <Zap size={24} className="text-accent-warning" />
              Native Token (NATIVE)
            </h3>
            <div className="space-y-4 text-text-secondary leading-relaxed">
              <p>
                NATIVE is the primary currency of the blockchain, used for transactions, mining rewards, and gas fees. It features controlled supply mechanisms through minting and burning.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="glass p-4 rounded-sm">
                  <h4 className="font-bold text-primary mb-2">Token Specifications</h4>
                  <ul className="text-sm space-y-1">
                    <li><span className="text-text-muted">Symbol:</span> NATIVE</li>
                    <li><span className="text-text-muted">Initial Supply:</span> 1,000,000</li>
                    <li><span className="text-text-muted">Decimals:</span> 18</li>
                    <li><span className="text-text-muted">Mining Reward:</span> 10 NATIVE</li>
                  </ul>
                </div>
                <div className="glass p-4 rounded-sm">
                  <h4 className="font-bold text-secondary mb-2">Token Operations</h4>
                  <ul className="text-sm space-y-1">
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-accent-success rounded-full" />
                      Transfer between wallets
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-accent-success rounded-full" />
                      Mint new tokens (admin)
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-accent-success rounded-full" />
                      Burn existing tokens
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-accent-success rounded-full" />
                      Mining rewards
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-8 pb-8 border-b border-border">
            <h3 className="font-unbounded text-2xl font-bold text-text-primary mb-4 flex items-center gap-3">
              <Lock size={24} className="text-accent-info" />
              Token Factory
            </h3>
            <div className="space-y-4 text-text-secondary leading-relaxed">
              <p>
                The Token Factory allows users to create custom tokens on the blockchain. Each token is independent with its own supply, name, and symbol, but operates within the same blockchain infrastructure.
              </p>
              <div className="glass p-4 rounded-sm">
                <h4 className="font-bold text-text-primary mb-2">Custom Token Features:</h4>
                <ul className="list-disc list-inside space-y-2 text-sm">
                  <li>User-defined symbol and name</li>
                  <li>Configurable total supply</li>
                  <li>Adjustable decimal places (default: 18)</li>
                  <li>Full supply allocated to creator on launch</li>
                  <li>Transfer between addresses</li>
                  <li>Tracked on the same blockchain</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-8 pb-8 border-b border-border">
            <h3 className="font-unbounded text-2xl font-bold text-text-primary mb-4 flex items-center gap-3">
              <Users size={24} className="text-primary" />
              Transaction Types
            </h3>
            <div className="space-y-3">
              <div className="glass p-4 rounded-sm">
                <h4 className="font-bold text-primary mb-1">Transfer</h4>
                <p className="text-text-secondary text-sm">Standard token transfer from one address to another. Requires valid signature and sufficient balance.</p>
              </div>
              <div className="glass p-4 rounded-sm">
                <h4 className="font-bold text-accent-success mb-1">Mint</h4>
                <p className="text-text-secondary text-sm">Create new NATIVE tokens and add them to an address. Used for mining rewards and authorized minting.</p>
              </div>
              <div className="glass p-4 rounded-sm">
                <h4 className="font-bold text-accent-error mb-1">Burn</h4>
                <p className="text-text-secondary text-sm">Permanently remove tokens from circulation by sending them to a null address (0x0).</p>
              </div>
              <div className="glass p-4 rounded-sm">
                <h4 className="font-bold text-secondary mb-1">Create Token</h4>
                <p className="text-text-secondary text-sm">Deploy a new custom token on the blockchain with specified parameters.</p>
              </div>
            </div>
          </section>

          <section className="mb-8 pb-8 border-b border-border">
            <h3 className="font-unbounded text-2xl font-bold text-text-primary mb-4 flex items-center gap-3">
              <TrendingUp size={24} className="text-accent-success" />
              Mining & Consensus
            </h3>
            <div className="space-y-4 text-text-secondary leading-relaxed">
              <p>
                The blockchain uses Proof-of-Work consensus where miners compete to find a valid nonce that produces a block hash meeting the difficulty requirement.
              </p>
              <div className="glass p-4 rounded-sm">
                <h4 className="font-bold text-text-primary mb-2">Mining Process:</h4>
                <ol className="list-decimal list-inside space-y-2 text-sm">
                  <li>Miner selects pending transactions from the pool</li>
                  <li>Creates a new block with transaction data and previous block hash</li>
                  <li>Incrementally tries different nonce values</li>
                  <li>Calculates SHA-256 hash for each nonce</li>
                  <li>When hash meets difficulty (leading zeros), block is valid</li>
                  <li>Miner receives block reward of 10 NATIVE tokens</li>
                  <li>Block is added to the chain and broadcast to network</li>
                </ol>
              </div>
              <div className="p-4 bg-background-subtle border border-border rounded-sm">
                <p className="text-xs text-text-muted">
                  <strong className="text-accent-warning">Current Difficulty:</strong> 4 leading zeros required
                  <br />
                  <strong className="text-accent-warning">Block Time:</strong> Variable (depends on network hash rate)
                  <br />
                  <strong className="text-accent-warning">Mining Reward:</strong> 10 NATIVE tokens per block
                </p>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h3 className="font-unbounded text-2xl font-bold text-text-primary mb-4">
              Technical Specifications
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="glass p-4 rounded-sm">
                <h4 className="font-bold text-text-primary mb-3">Blockchain</h4>
                <ul className="space-y-2 text-sm text-text-secondary">
                  <li><span className="text-text-muted">Consensus:</span> Proof-of-Work</li>
                  <li><span className="text-text-muted">Hashing:</span> SHA-256</li>
                  <li><span className="text-text-muted">Block Structure:</span> Index, Timestamp, Transactions, Hash, Nonce</li>
                  <li><span className="text-text-muted">Chain Validation:</span> Full history verification</li>
                </ul>
              </div>
              <div className="glass p-4 rounded-sm">
                <h4 className="font-bold text-text-primary mb-3">Cryptography</h4>
                <ul className="space-y-2 text-sm text-text-secondary">
                  <li><span className="text-text-muted">Key Generation:</span> ECDSA SECP256k1</li>
                  <li><span className="text-text-muted">Signatures:</span> ECDSA</li>
                  <li><span className="text-text-muted">Address Format:</span> Hex-encoded public key</li>
                  <li><span className="text-text-muted">Transaction Hash:</span> SHA-256</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="glass p-6 rounded-sm bg-primary/5">
            <h3 className="font-unbounded text-xl font-bold text-primary mb-3">
              Use Cases & Applications
            </h3>
            <ul className="space-y-2 text-sm text-text-secondary">
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2" />
                <span>
                  <strong className="text-text-primary">Digital Payments & Settlement:</strong>
                  Enable peer-to-peer and merchant transactions with cryptographic security and deterministic finality.
                </span>
              </li>

              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2" />
                <span>
                  <strong className="text-text-primary">Tokenization Infrastructure:</strong>
                  Issue and manage digital assets including utility tokens, governance tokens, and real-world asset representations.
                </span>
              </li>

              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2" />
                <span>
                  <strong className="text-text-primary">Decentralized Finance (DeFi):</strong>
                  Support staking, liquidity provisioning, programmable value transfer, and incentive-driven economic models.
                </span>
              </li>

              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2" />
                <span>
                  <strong className="text-text-primary">Enterprise & System Integration:</strong>
                  Integrate blockchain-backed verification, audit trails, and digital asset workflows into external applications and business infrastructure.
                </span>
              </li>
            </ul>
          </section>
        </div>
      </motion.div>
    </div>
  );
};

export default Whitepaper;
